import type { ReactNode } from "react";

import { cn } from "../../lib/utils.js";

export interface ProgressRingProps {
  /** Share of the goal reached. Past 100 the ring stays full and the label carries the real figure. */
  percent: number;
  /** Diameter in px. */
  size?: number;
  strokeWidth?: number;
  /** Arc colour as a CSS colour, e.g. `var(--color-success)`. */
  color: string;
  className?: string;
  /** Text in the centre of the ring. */
  children?: ReactNode;
}

/** A circular gauge: the arc fills to `percent`, with an optional label in its centre. */
function ProgressRing({ percent, size = 36, strokeWidth = 3, color, className, children }: ProgressRingProps) {
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = (Math.min(Math.max(percent, 0), 100) / 100) * circumference;

  return (
    <div className={cn("relative inline-flex shrink-0 items-center justify-center", className)}>
      <svg width={size} height={size} aria-hidden="true">
        <circle cx={center} cy={center} r={radius} fill="none" stroke="var(--color-surface-200)" strokeWidth={strokeWidth} />
        {filled > 0 && (
          <circle
            cx={center} cy={center} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"
            strokeDasharray={`${filled} ${circumference - filled}`}
            transform={`rotate(-90 ${center} ${center})`}
          />
        )}
      </svg>
      {children != null && (
        <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold leading-none">{children}</span>
      )}
    </div>
  );
}

export { ProgressRing };
